"use client";

import React from "react";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Mail, Copy, MoreHorizontal } from "lucide-react";
import { AttendeeAvatar } from "./AttendeeAvatar";
import { AttendeeStatusBadge } from "./AttendeeStatusBadge";
import { AttendeeChip } from "../attendees/AttendeeChip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../tooltip";
import { useToast } from "../../../hooks/use-toast";

interface AttendeeItemProps {
  email: string;
  name?: string;
  responseStatus?: string;
  isOrganizer?: boolean;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  showActions?: boolean;
  variant?: "compact" | "detailed" | "chip";
  className?: string;
  ContactPopoverComponent?: React.ComponentType<{ name: string; email: string; children: React.ReactNode }>;
}

export function AttendeeItem({
  email,
  name,
  responseStatus,
  isOrganizer = false,
  onComposeEmail,
  showActions = true,
  variant = "detailed",
  className = "",
  ContactPopoverComponent
}: AttendeeItemProps) {
  const { toast } = useToast();

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: "Email copied",
        description: "Email address copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy email to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleSendEmail = () => {
    if (onComposeEmail) {
      onComposeEmail([email]);
    } else {
      window.open(`mailto:${email}`, '_blank');
    }
  };

  const displayName = name || email;

  if (variant === "chip") {
    return (
      <AttendeeChip
        email={email}
        name={name}
        variant="secondary"
        size="sm"
        clickable={true}
        className={className}
        ContactPopoverComponent={ContactPopoverComponent}
      />
    );
  }

  if (variant === "compact") {
    return (
      <div className={`flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors ${className}`}>
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <AttendeeAvatar name={name} email={email} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{displayName}</p>
            {name && (
              <p className="text-xs text-muted-foreground truncate">{email}</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-1 shrink-0">
          {isOrganizer && (
            <Badge variant="secondary" className="text-xs">
              Organizer
            </Badge>
          )}
          <AttendeeStatusBadge status={responseStatus} size="sm" />
          {showActions && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSendEmail}
                  className="h-6 w-6 p-0"
                  aria-label="Send email"
                >
                  <Mail className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Send email</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-card-50 transition-colors ${className}`}>
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <AttendeeAvatar name={name} email={email} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="font-medium truncate">
              {displayName}
            </h3>
            {isOrganizer && (
              <Badge variant="secondary" className="text-xs">
                Organizer
              </Badge>
            )}
          </div>
          {name && (
            <p className="text-sm text-muted-foreground truncate">
              {email}
            </p>
          )}
          <div className="flex items-center space-x-2 mt-1">
            <AttendeeStatusBadge 
              status={responseStatus} 
              showIcon={true}
              size="sm"
            />
          </div>
        </div>
      </div>

      {showActions && (
        <div className="flex items-center space-x-2 shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyEmail}
                className="h-8 w-8 p-0"
                aria-label="Copy email address"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy email address</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" aria-label="Open attendee actions menu">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>Open attendee actions menu</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleSendEmail}>
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyEmail}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Email
              </DropdownMenuItem>
              {name && (
                <DropdownMenuItem onClick={() => {
                  const text = `${name} (${email})`;
                  navigator.clipboard.writeText(text);
                  toast({
                    title: "Contact info copied",
                    description: "Name and email copied to clipboard",
                  });
                }}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Name & Email
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
